import React, { useMemo } from "react";
import { cn } from "../../../util/general";

const WIDTH = 64;
const HEIGHT = 22;

const buildSparklinePoints = (values, width, height) => {
  if (!values.length) {
    return "";
  }

  const maxValue = Math.max(...values, 1);
  const minValue = Math.min(...values, 0);
  const range = maxValue - minValue || 1;

  return values
    .map((value, index) => {
      const x = values.length === 1 ? width / 2 : (index / (values.length - 1)) * width;
      const y = height - ((value - minValue) / range) * height;
      return `${x},${y}`;
    })
    .join(" ");
};

const buildSmoothPath = (coordinates) => {
  if (!coordinates.length) {
    return "";
  }

  if (coordinates.length === 1) {
    return `M ${coordinates[0].x} ${coordinates[0].y}`;
  }

  return coordinates.reduce((path, point, index, points) => {
    if (index === 0) {
      return `M ${point.x} ${point.y}`;
    }

    const previousPoint = points[index - 1];
    const controlX = (previousPoint.x + point.x) / 2;
    return `${path} C ${controlX} ${previousPoint.y}, ${controlX} ${point.y}, ${point.x} ${point.y}`;
  }, "");
};

const buildAreaPath = (coordinates) => {
  if (!coordinates.length) {
    return "";
  }

  const smoothPath = buildSmoothPath(coordinates);
  const firstPoint = coordinates[0];
  const lastPoint = coordinates[coordinates.length - 1];

  return `${smoothPath} L ${lastPoint.x} ${HEIGHT} L ${firstPoint.x} ${HEIGHT} Z`;
};

const MiniTrendSparkline = ({ data = [], className }) => {
  const values = useMemo(
    () =>
      data
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value)),
    [data]
  );

  const points = useMemo(() => buildSparklinePoints(values, WIDTH, HEIGHT), [values]);

  const coordinates = useMemo(
    () =>
      points
        ? points.split(" ").map((point) => {
            const [x, y] = point.split(",").map(Number);
            return { x, y };
          })
        : [],
    [points]
  );

  const smoothPath = useMemo(() => buildSmoothPath(coordinates), [coordinates]);
  const areaPath = useMemo(() => buildAreaPath(coordinates), [coordinates]);

  if (!coordinates.length) {
    return null;
  }

  return (
    <div className={cn("pointer-events-none", className)}>
      <svg width={WIDTH} height={HEIGHT} viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="overflow-visible">
        <defs>
          <linearGradient id="mini-trend-sparkline-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(255,255,255,0.24)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#mini-trend-sparkline-fill)" />
        <path
          d={smoothPath}
          fill="none"
          stroke="rgba(255,255,255,0.5)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {coordinates.map(({ x, y }, index) => (
          <circle
            key={`${x}-${y}-${index}`}
            cx={x}
            cy={y}
            r="2"
            fill="rgba(255,255,255,0.62)"
          />
        ))}
      </svg>
    </div>
  );
};

export default MiniTrendSparkline;
